import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FiltroRelatorioTarefa, RelatorioTarefa } from 'src/app/shared/interfaces/tarefa/relatorio-tarefa.interface';
import { TarefaReiniciarColeta } from 'src/app/shared/interfaces/tarefa/tarefa-reiniciar-coleta.interface';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class TarefaPedidoAPIService {

  private baseUrl = `${environment.APIEndpoint}tarefa`;
  private baseUrlRelatorio = `${environment.APIEndpoint}tarefarelatorio`;

  constructor(private http: HttpClient) { }

  getAll(pageNumber: number = 1, pageSize: number = 100, pedidoId: number, setorId?: number, statusId?: string, usuarioId?: number) {
    var queryParams = `?pageNumber=${pageNumber}&pageSize=${pageSize}`;

    if(setorId != null && setorId != undefined)
      queryParams += `&setorId=${setorId}`;

    if(statusId != null && statusId != undefined)
      queryParams += `&statusId=${statusId}`;

    if(usuarioId != null && usuarioId != undefined)
      queryParams += `&usuarioId=${usuarioId}`;

    return this.http.get(`${this.baseUrl}/pedido/${pedidoId}${queryParams}`);
  }

  getTarefasPedidos(
    pedidoId: number,
    pageNumber: number = 1,
    pageSize: number = 100,
    setorId?: number,
    statusId?: string,
    usuarioId?: number
  ) {
    let queryParams = `?pageNumber=${pageNumber}&pageSize=${pageSize}`;

    if(setorId != null && setorId != undefined)
      queryParams += `&setorId=${setorId}`;

    if(statusId != null && statusId != undefined)
      queryParams += `&statusId=${statusId}`;

    if(usuarioId != null && usuarioId != undefined)
      queryParams += `&usuarioId=${usuarioId}`;

    return this.http.get(`${this.baseUrl}/pedido/${pedidoId}${queryParams}`);
  }

  getById(id: number) {
    return this.http.get(`${this.baseUrl}/${id}`);
  }

  create(data: any) {
    return this.http.post(this.baseUrl, data);
  }

  update(id: number, data: any) {
    return this.http.put(`${this.baseUrl}/${id}`, data);
  }

  delete(id: number) {
    return this.http.delete(`${this.baseUrl}/${id}`);
  }

  iniciarTarefa(id: number, observacao: string) {
    return this.http.put(`${this.baseUrl}/${id}/iniciar`, { Observacao: observacao });
  }

  iniciarTarefasEmLote(ids: number[], observacao: string) {
    return this.http.put(`${this.baseUrl}/iniciar/lote`, { Ids: ids, Observacao: observacao });
  }

  pausarTarefa(id: number, observacao: string) {
    return this.http.put(`${this.baseUrl}/${id}/pausar`, { Observacao: observacao });
  }

  pausarTarefasEmLote(ids: number[], observacao: string) {
    return this.http.put(`${this.baseUrl}/pausar/lote`, { Ids: ids, Observacao: observacao });
  }

  finalizarTarefa(id: number, observacao: string) {
    return this.http.put(`${this.baseUrl}/${id}/finalizar`, { Observacao: observacao });
  }

  finalizarTarefasEmLote(ids: number[], observacao: string) {
    return this.http.put(`${this.baseUrl}/finalizar/lote`, { Ids: ids, Observacao: observacao });
  }

  /**
   * Finaliza a tarefa pelo endpoint `/app` — quando o setor exige medição,
   * o backend desvia a tarefa para o status 8 (Aguardando Medidas) em vez
   * de finalizar direto. Isso prepara a tarefa para o POST /tarefa-medicao
   * que só aceita tarefas em status 8.
   *
   * `quantidade` é obrigatório quando o setor NÃO exige medição nem foto —
   * sem ele, o backend completa o restante automaticamente e finaliza
   * inteira (em vez de respeitar quanto foi realmente feito).
   */
  finalizarTarefaApp(id: number, observacao: string = '', quantidade?: number) {
    const payload: any = { Observacao: observacao };
    if (quantidade != null) payload.Quantidade = quantidade;
    return this.http.put(`${this.baseUrl}/${id}/finalizar/app`, payload);
  }

  /**
   * Versão em lote do `/finalizar/app`. Cada tarefa é avaliada individualmente
   * (vai para 4 ou 8 conforme `setor.requer_medicao`).
   *
   * `quantidades` mapeia { tarefaId: qty }. Obrigatório para tarefas de
   * setor SEM medição/foto pelo mesmo motivo do método acima.
   */
  finalizarTarefasEmLoteApp(
    ids: number[],
    observacao: string = '',
    quantidades?: { [tarefaId: number]: number },
  ) {
    const payload: any = { Ids: ids, Observacao: observacao };
    if (quantidades && Object.keys(quantidades).length > 0) {
      payload.Quantidades = quantidades;
    }
    return this.http.put(`${this.baseUrl}/finalizar/lote/app`, payload);
  }

  naoConformeTarefa(id: number, observacao: string) {
    return this.http.put(`${this.baseUrl}/${id}/nao-conforme`, { Observacao: observacao });
  }

  naoConformeTarefasEmLote(ids: number[], observacao: string) {
    return this.http.put(`${this.baseUrl}/nao-conforme/lote`, { Ids: ids, Observacao: observacao });
  }

  reiniciarTarefa(id: number, observacao: string) {
    return this.http.put(`${this.baseUrl}/${id}/reiniciar`, { Observacao: observacao });
  }

  /**
   * Reverte a tarefa para "Em Produção" (2). Permitido apenas para tarefas
   * Finalizadas (4) ou Aguardando Medidas (8), de setores ≠ Logística.
   */
  voltarStatusTarefa(id: number) {
    return this.http.put(`${this.baseUrl}/${id}/voltar-status`, {});
  }

  divergenciaTarefa(id: number, observacao: string, gerarTarefaCompras?: boolean, quantidadeCompra?: number | null) {
    const payload: any = { Observacao: observacao };
    
    // Adiciona os campos opcionais se fornecidos
    if (gerarTarefaCompras !== undefined) {
      payload.GerarTarefaCompras = gerarTarefaCompras;
    }
    
    if (quantidadeCompra !== undefined && quantidadeCompra !== null) {
      payload.QuantidadeCompra = quantidadeCompra;
    }
    
    return this.http.put(`${this.baseUrl}/${id}/divergencia`, payload);
  }

  reiniciarDivergenciaTarefa(id: number, observacao: string) {
    return this.http.put(`${this.baseUrl}/${id}/reiniciar-divergencia`, { Observacao: observacao });
  }

  reiniciarColeta(tarefaId: number, pedidoId: number){
    let reiniciarColeta: TarefaReiniciarColeta;
      reiniciarColeta = {
        pedidoId: pedidoId,
        tarefaId: tarefaId
      };
    return this.http.post(`${this.baseUrl}/cancelar-coleta-tarefa`, reiniciarColeta);
  }

  criarNovasTarefas(command: any) {
    return this.http.post(`${this.baseUrl}/criar-novas-tarefas`, command);
  }

  getTarefasFinalizadasHoje(pedidoId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/finalizadas-hoje/${pedidoId}`);
  }

  getMinhasTarefas(statusId?: number, setorId?: number) {
    let queryParams = '';
    const params: string[] = [];

    if (statusId != null && statusId != undefined) {
      params.push(`statusId=${statusId}`);
    }

    if (setorId != null && setorId != undefined) {
      params.push(`setorId=${setorId}`);
    }

    if (params.length > 0) {
      queryParams = '?' + params.join('&');
    }

    return this.http.get(`${this.baseUrl}/minhas-tarefas${queryParams}`);
  }

  getHistorico(pedidoId: number, produtoId: number) {
    return this.http.get(`${this.baseUrl}/historico?pedidoId=${pedidoId}&produtoId=${produtoId}`);
  }

  /**
   * Busca as tarefas de um produto específico dentro de um pedido
   * @param pedidoId ID do pedido
   * @param produtoId ID do produto
   * @returns Observable com array de TarefaPedido
   */
  getTarefasPorProdutoItem(pedidoId: number, produtoId: number, produtoPaiId: number = 0) {
    return this.http.get(`${this.baseUrl}/pedido/${pedidoId}/${produtoId}?produtoPaiId=${produtoPaiId}`);
  }

  getTarefasDivergenciaNaoConforme(
    pageNumber: number = 1,
    pageSize: number = 11,
    setorId?: number,
    statusId?: number,
    usuarioId?: number
  ) {
    const params: any = {
      pageNumber: pageNumber,
      pageSize: pageSize
    };

    if (setorId != null && setorId != undefined) {
      params.setorId = setorId;
    }

    if (statusId != null && statusId != undefined) {
      params.statusId = statusId;
    }

    if (usuarioId != null && usuarioId != undefined) {
      params.usuarioId = usuarioId;
    }

    return this.http.get(`${this.baseUrl}/tarefa-em-divergencia-nao-conforme`, { params });
  }

  atualizarUsuarioEmLote(pedidoId: number, setorId: number, usuarioAtribuidoId: number) {
    const payload = {
      setorId: setorId,
      usuarioAtribuidoId: usuarioAtribuidoId
    };
    return this.http.put(`${this.baseUrl}/${pedidoId}/atualizar-usuario-em-lote`, payload);
  }

  atualizarUsuarioPorIds(tarefaIds: number[], usuarioAtribuidoId: number | null) {
    return this.http.put(`${this.baseUrl}/atualizar-usuario-por-ids`, {
      tarefaIds,
      usuarioAtribuidoId
    });
  }

  finalizarTarefaManual(id: number, observacao: string | null) {
    return this.http.put(`${this.baseUrl}/${id}/finalizar-manual`, { observacao });
  }

  getCadeiaFrente(tarefaId: number) {
    return this.http.get(`${this.baseUrl}/${tarefaId}/cadeia-frente`);
  }

  gerarTarefaComReordenacao(payload: {
    tarefaOrigemId: number;
    setorId: number;
    usuarioAtribuidoId: number;
    ordemFinal: number[];
  }) {
    return this.http.post(`${this.baseUrl}/gerar-com-reordenacao`, payload);
  }

  corrigirItemPaiTarefa(pedidoItemId: number, pedidoId: number, produtoId: number, produtoPaiId: number) {
    const payload = {
      pedidoItemId: pedidoItemId,
      pedidoId: pedidoId,
      produtoId: produtoId,
      produtoPaiId: produtoPaiId,
    };

    return this.http.put(`${this.baseUrl}/corrigir-produto-pai`, payload);
  }

  getRelatorio(filtro: FiltroRelatorioTarefa): Observable<RelatorioTarefa[]> {
    const params: string[] = [];

    if (filtro.dataInicio) params.push(`dataInicio=${filtro.dataInicio}`);
    if (filtro.dataFim) params.push(`dataFim=${filtro.dataFim}`);
    if (filtro.tipoData) params.push(`tipoData=${filtro.tipoData}`);
    if (filtro.pedidoNumero != null) params.push(`pedidoNumero=${filtro.pedidoNumero}`);
    if (filtro.pedidoParte != null) params.push(`pedidoParte=${filtro.pedidoParte}`);
    if (filtro.identificacao) params.push(`identificacao=${encodeURIComponent(filtro.identificacao)}`);
    if (filtro.usuarioId != null) params.push(`usuarioId=${filtro.usuarioId}`);
    if (filtro.setorId != null) params.push(`setorId=${filtro.setorId}`);
    if (filtro.statusId != null) params.push(`statusId=${filtro.statusId}`);
    if (filtro.prioridadeId != null) params.push(`prioridadeId=${filtro.prioridadeId}`);

    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    return this.http.get<RelatorioTarefa[]>(`${this.baseUrlRelatorio}/listar${queryString}`);
  }

  /** Histórico de eventos de uma tarefa (status, datas, usuário, observação) */
  getHistoricoEventosTarefa(tarefaId: number): Observable<TarefaEvento[]> {
    return this.http.get<TarefaEvento[]>(
      `${this.baseUrlRelatorio}/historico-events/${tarefaId}`,
    );
  }
}

export interface TarefaEvento {
  tarefaId: number;
  statusAnteriorId: number;
  statusAnteriorNome: string;
  statusNovoId: number;
  statusNovoNome: string;
  usuarioAtribuidoId?: number;
  usuarioAtribuidoNome?: string;
  dataEvento: string;
  observacao?: string;
}
